
import React, { useState, useEffect, useCallback, useRef } from "react"
import { Button, Form, Row, Col, Alert, InputGroup, ListGroup } from "react-bootstrap"

/**
 * @typedef {object} LocationData
 * @property {string} latitude
 * @property {string} longitude
 * @property {string} [address]
 * @property {string} [city]
 * @property {string} [state]
 * @property {string} [country]
 * @property {string} [zipCode]
 * @property {string} [displayName]
 * @property {string} [placeType]
 */

/**
 * @typedef {object} GoogleMapsSelectorProps
 * @property {string} [initialLatitude]
 * @property {string} [initialLongitude]
 * @property {string} [initialAddress]
 * @property {(locationData: LocationData) => void} onLocationChange
 * @property {(address: string) => void} [onAddressChange]
 * @property {string} googleMapsApiKey
 * @property {string} [mapHeight]
 * @property {number} [defaultZoom]
 * @property {string} [className]
 * @property {boolean} [disabled]
 */

/**
 * A Google Maps component for selecting a location via search, map click, or current location.
 * @param {GoogleMapsSelectorProps} props
 */
const GoogleMapsSelector = ({
  initialLatitude = "",
  initialLongitude = "",
  initialAddress = "",
  onLocationChange,
  onAddressChange,
  googleMapsApiKey,
  mapHeight = "400px",
  defaultZoom = 15,
  className = "",
  disabled = false,
  hideInstructions = false,
}) => {
  const [map, setMap] = useState(null)
  const [marker, setMarker] = useState(null)
  const [autocompleteService, setAutocompleteService] = useState(null)
  const [placesService, setPlacesService] = useState(null)
  const [geocoder, setGeocoder] = useState(null)

  const [searchAddress, setSearchAddress] = useState(initialAddress)
  const [suggestions, setSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1)

  const [coordinates, setCoordinates] = useState({
    latitude: initialLatitude,
    longitude: initialLongitude,
  })

  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [isGettingLocation, setIsGettingLocation] = useState(false)

  const mapRef = useRef(null)
  const debounceRef = useRef()

  // Initialize Google Maps
  useEffect(() => {
    const initMap = () => {
      if (!mapRef.current || !window.google) return

      const initialCenter = {
        lat: initialLatitude ? Number.parseFloat(initialLatitude) : 40.7128,
        lng: initialLongitude ? Number.parseFloat(initialLongitude) : -74.006,
      }

      const mapInstance = new window.google.maps.Map(mapRef.current, {
        center: initialCenter,
        zoom: defaultZoom,
        mapTypeControl: true,
        streetViewControl: true,
        fullscreenControl: true,
      })

      const markerInstance = new window.google.maps.Marker({
        position: initialLatitude && initialLongitude ? initialCenter : undefined,
        map: mapInstance,
        draggable: !disabled,
        title: "Selected Location",
      })

      // Map click handler
      mapInstance.addListener("click", e => {
        if (disabled || !e.latLng) return

        const lat = e.latLng.lat()
        const lng = e.latLng.lng()

        markerInstance.setPosition({ lat, lng })
        setCoordinates({
          latitude: lat.toFixed(8),
          longitude: lng.toFixed(8),
        })

        // Reverse geocode
        reverseGeocode(lat, lng)
      })

      // Marker drag handler
      markerInstance.addListener("dragend", e => {
        if (!e.latLng) return

        const lat = e.latLng.lat()
        const lng = e.latLng.lng()

        setCoordinates({
          latitude: lat.toFixed(8),
          longitude: lng.toFixed(8),
        })

        reverseGeocode(lat, lng)
      })

      setMap(mapInstance)
      setMarker(markerInstance)

      // Initialize services
      setAutocompleteService(new window.google.maps.places.AutocompleteService())
      setPlacesService(new window.google.maps.places.PlacesService(mapInstance))
      setGeocoder(new window.google.maps.Geocoder())
    }

    // Load Google Maps script if not already loaded
    if (!window.google) {
      const script = document.createElement("script")
      script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=places`
      script.async = true
      script.defer = true
      script.onload = initMap
      document.head.appendChild(script)
    } else {
      initMap()
    }
  }, [googleMapsApiKey, initialLatitude, initialLongitude, defaultZoom, disabled])
// useEffect(() => {
//   const initMap = () => {
//     if (!mapRef.current || !window.google) return;

//     const initialCenter = {
//       lat: initialLatitude ? Number.parseFloat(initialLatitude) : 40.7128,
//       lng: initialLongitude ? Number.parseFloat(initialLongitude) : -74.006,
//     };

//     const mapInstance = new window.google.maps.Map(mapRef.current, {
//       center: initialCenter,
//       zoom: defaultZoom,
//       mapTypeControl: true,
//       streetViewControl: true,
//       fullscreenControl: true,
//     });

//     const markerInstance = new window.google.maps.Marker({
//       position: initialLatitude && initialLongitude ? initialCenter : undefined,
//       map: mapInstance,
//       draggable: !disabled,
//       title: "Selected Location",
//     });

//     // ✅ If initial coords exist, fire reverse geocode + onLocationChange immediately
//     if (initialLatitude && initialLongitude) {
//       reverseGeocode(Number(initialLatitude), Number(initialLongitude));
//       onLocationChange?.({
//         latitude: Number(initialLatitude),
//         longitude: Number(initialLongitude),
//       });
//     }

//     // Map click handler
//     mapInstance.addListener("click", (e) => {
//       if (disabled || !e.latLng) return;

//       const lat = e.latLng.lat();
//       const lng = e.latLng.lng();

//       markerInstance.setPosition({ lat, lng });
//       setCoordinates({
//         latitude: lat.toFixed(8),
//         longitude: lng.toFixed(8),
//       });

//       reverseGeocode(lat, lng);
//       onLocationChange?.({
//         latitude: lat.toFixed(8),
//         longitude: lng.toFixed(8),
//       });
//     });

//     // Marker drag handler
//     markerInstance.addListener("dragend", (e) => {
//       if (!e.latLng) return;

//       const lat = e.latLng.lat();
//       const lng = e.latLng.lng();

//       setCoordinates({
//         latitude: lat.toFixed(8),
//         longitude: lng.toFixed(8),
//       });

//       reverseGeocode(lat, lng);
//       onLocationChange?.({
//         latitude: lat.toFixed(8),
//         longitude: lng.toFixed(8),
//       });
//     });

//     setMap(mapInstance);
//     setMarker(markerInstance);

//     // Initialize services
//     setAutocompleteService(new window.google.maps.places.AutocompleteService());
//     setPlacesService(new window.google.maps.places.PlacesService(mapInstance));
//     setGeocoder(new window.google.maps.Geocoder());
//   };

//   // Load Google Maps script if not already loaded
//   if (!window.google) {
//     const script = document.createElement("script");
//     script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=places`;
//     script.async = true;
//     script.defer = true;
//     script.onload = initMap;
//     document.head.appendChild(script);
//   } else {
//     initMap();
//   }
// }, [googleMapsApiKey, defaultZoom, disabled,initialLatitude, initialLongitude]);

  // Reverse geocoding
  const reverseGeocode = useCallback(
    (lat, lng) => {
      if (!geocoder) return

      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results && results[0]) {
          const result = results[0]
          const locationData = {
            latitude: lat.toFixed(8),
            longitude: lng.toFixed(8),
            address: result.formatted_address,
            displayName: result.formatted_address,
          }

          // Extract address components
          result.address_components?.forEach(component => {
            if (component.types.includes("locality")) {
              locationData.city = component.long_name
            } else if (component.types.includes("administrative_area_level_1")) {
              locationData.state = component.short_name
            } else if (component.types.includes("country")) {
              locationData.country = component.long_name
            } else if (component.types.includes("postal_code")) {
              locationData.zipCode = component.long_name
            }
          })

          setSearchAddress(result.formatted_address)
          onLocationChange(locationData)
          onAddressChange?.(result.formatted_address)
        }
      })
    },
    [geocoder, onLocationChange, onAddressChange]
  )

  // Autocomplete search
  const searchWithAutocomplete = useCallback(
    query => {
      if (!autocompleteService || !query.trim() || query.length < 3) {
        setSuggestions([])
        setShowSuggestions(false)
        return
      }

      setIsSearching(true)

      const request = {
        input: query,
        types: ["establishment", "geocode"], // Include businesses and addresses
        componentRestrictions: { country: "us" }, // Adjust as needed
      }

      autocompleteService.getPlacePredictions(request, (predictions, status) => {
        setIsSearching(false)

        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setSuggestions(predictions)
          setShowSuggestions(true)
        } else {
          setSuggestions([])
          setShowSuggestions(false)
        }
      })
    },
    [autocompleteService]
  )

  // Handle search input change
  const handleSearchChange = value => {
    setSearchAddress(value)
    onAddressChange?.(value)

    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    debounceRef.current = setTimeout(() => {
      searchWithAutocomplete(value)
    }, 300)
  }

  // Select suggestion
  const selectSuggestion = useCallback(
    prediction => {
      if (!placesService || !map || !marker) return

      setSearchAddress(prediction.description)
      setShowSuggestions(false)
      setSuggestions([])
      setSelectedSuggestionIndex(-1)

      const request = {
        placeId: prediction.place_id,
        fields: ["geometry", "formatted_address", "name", "types", "address_components"],
      }

      placesService.getDetails(request, (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place && place.geometry?.location) {
          const lat = place.geometry.location.lat()
          const lng = place.geometry.location.lng()

          marker.setPosition({ lat, lng })
          map.setCenter({ lat, lng })
          map.setZoom(17) // Zoom in for better detail

          setCoordinates({
            latitude: lat.toFixed(8),
            longitude: lng.toFixed(8),
          })

          const locationData = {
            latitude: lat.toFixed(8),
            longitude: lng.toFixed(8),
            address: place.formatted_address || prediction.description,
            displayName: place.name || place.formatted_address || prediction.description,
            placeType: place.types?.[0] || "",
          }

          // Extract address components
          place.address_components?.forEach(component => {
            if (component.types.includes("locality")) {
              locationData.city = component.long_name
            } else if (component.types.includes("administrative_area_level_1")) {
              locationData.state = component.short_name
            } else if (component.types.includes("country")) {
              locationData.country = component.long_name
            } else if (component.types.includes("postal_code")) {
              locationData.zipCode = component.long_name
            }
          })

          onLocationChange(locationData)
          setSuccess("Location selected successfully!")
          setTimeout(() => setSuccess(""), 3000)
        }
      })
    },
    [placesService, map, marker, onLocationChange]
  )

  // Handle keyboard navigation
  const handleKeyDown = e => {
    if (!showSuggestions || suggestions.length === 0) return

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault()
        setSelectedSuggestionIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : prev))
        break
      case "ArrowUp":
        e.preventDefault()
        setSelectedSuggestionIndex(prev => (prev > 0 ? prev - 1 : prev))
        break
      case "Enter":
        e.preventDefault()
        if (selectedSuggestionIndex >= 0) {
          selectSuggestion(suggestions[selectedSuggestionIndex])
        }
        break
      case "Escape":
        setShowSuggestions(false)
        setSelectedSuggestionIndex(-1)
        break
    }
  }

  // Get current location
  const getCurrentLocation = () => {
    if (!navigator.geolocation || disabled || !map || !marker) {
      setError("Geolocation is not supported by this browser.")
      return
    }

    setIsGettingLocation(true)
    setError("")

    navigator.geolocation.getCurrentPosition(
      position => {
        const { latitude: lat, longitude: lng } = position.coords

        marker.setPosition({ lat, lng })
        map.setCenter({ lat, lng })
        map.setZoom(17)

        setCoordinates({
          latitude: lat.toFixed(8),
          longitude: lng.toFixed(8),
        })

        reverseGeocode(lat, lng)
        setSuccess("Current location detected successfully!")
        setTimeout(() => setSuccess(""), 3000)
        setIsGettingLocation(false)
      },
      error => {
        let errorMessage = "Failed to get current location."
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = "Location access denied by user."
            break
          case error.POSITION_UNAVAILABLE:
            errorMessage = "Location information is unavailable."
            break
          case error.TIMEOUT:
            errorMessage = "Location request timed out."
            break
        }
        setError(errorMessage)
        setIsGettingLocation(false)
      }
    )
  }

  // Format suggestion display
  const formatSuggestionDisplay = prediction => {
    const mainText = prediction.structured_formatting.main_text
    const secondaryText = prediction.structured_formatting.secondary_text

    return (
      <div>
        <div className="fw-bold">{mainText}</div>
        <small className="text-muted">{secondaryText}</small>
        <small className="badge bg-primary ms-2">{prediction.types[0]}</small>
      </div>
    )
  }

  return (
    <div className={`google-maps-selector ${className}`}>
      {/* Search and Controls */}
      <Row className="mb-3">
        <Col md={8}>
          <Form.Group>
            <Form.Label>Search Location</Form.Label>
            <div className="position-relative">
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder="Enter address, business name, or landmark"
                  value={searchAddress}
                  onChange={e => handleSearchChange(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                  disabled={disabled}
                />
                <Button variant="outline-primary" disabled={isSearching || disabled}>
                  {isSearching ? "🔄" : "🔍"}
                </Button>
              </InputGroup>

              {/* Autocomplete Suggestions */}
              {showSuggestions && suggestions.length > 0 && (
                <div
                  className="position-absolute w-100 bg-white border rounded shadow-lg"
                  style={{ zIndex: 1000, maxHeight: "300px", overflowY: "auto" }}
                >
                  <ListGroup variant="flush">
                    {suggestions.map((prediction, index) => (
                      <ListGroup.Item
                        key={prediction.place_id}
                        action
                        active={index === selectedSuggestionIndex}
                        onClick={() => selectSuggestion(prediction)}
                        className="cursor-pointer"
                      >
                        {formatSuggestionDisplay(prediction)}
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </div>
              )}
            </div>
          </Form.Group>
        </Col>
        <Col md={4} className="d-flex align-items-end">
          <Button
            variant="outline-success curr_location-btn"
            onClick={getCurrentLocation}
            disabled={isGettingLocation || disabled}
            className=" w-100"
          >
            {isGettingLocation ? "Getting Location..." : "📍 Current Location"}
          </Button>
        </Col>
      </Row>

      {/* Error and Success Messages */}
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" className="mb-3">
          {success}
        </Alert>
      )}

      {/* Map Container */}
      <div ref={mapRef} style={{ height: mapHeight, width: "100%" }} className="border rounded mb-3 map_h" />

      {/* Coordinate Display */}
      <Row>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Latitude</Form.Label>
            <Form.Control type="text" value={coordinates.latitude} readOnly />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Longitude</Form.Label>
            <Form.Control type="text" value={coordinates.longitude} readOnly />
          </Form.Group>
        </Col>
      </Row>

      {/* Instructions */}
     {!hideInstructions? <div className="mt-3">
        <small className="text-muted">
          <strong>Instructions:</strong>
          <ul className="mb-0 mt-1">
            <li>Type to search for businesses, addresses, or landmarks</li>
            <li>Click on the map or drag the marker to select a location</li>
            <li>Use "Current Location" to automatically detect your position</li>
          </ul>
        </small>
      </div>:""}
    </div>
  )
}

export default GoogleMapsSelector